import type { LiteralUnion } from 'type-fest';
import { DEFAULT_NAMESPACE } from './constants/constants';
import type {
  MasonryEngineNamespacedNodeRendererMap,
  MasonryEngineNode,
  MasonryEngineNodeRenderer,
  MasonryEngineNodeRendererMap,
} from './types/masonry-engine-node.types';
import type {
  MasonryEngineNodeRenderersRegistry,
  RendererNodeForType,
} from './types/masonry-engine-driver.types';

export const createMasonryEngineNodeRenderersRegistry = <
  Node extends MasonryEngineNode,
>(
  defaultNodeRendererMap: MasonryEngineNodeRendererMap<Node>,
): MasonryEngineNodeRenderersRegistry<Node> => {
  const nodeRendererMap: MasonryEngineNamespacedNodeRendererMap<Node> = {
    [DEFAULT_NAMESPACE]: defaultNodeRendererMap,
  };

  return {
    nodeRenderers: nodeRendererMap,

    registerRenderer<Type extends LiteralUnion<Node['type'], string>>(
      type: Type,
      namespace: string,
      renderer: MasonryEngineNodeRenderer<RendererNodeForType<Node, Type>>,
    ) {
      if (!nodeRendererMap[namespace]) {
        nodeRendererMap[namespace] = {};
      }
      const namespacedRendererMap = nodeRendererMap[namespace] as Record<
        string,
        unknown
      >;
      if (namespacedRendererMap[type]) {
        throw new Error(
          'Attempting to register a renderer for a type at a namespace where a renderer was already registered for that type!',
        );
      }
      namespacedRendererMap[type] = renderer as unknown;
    },

    overrideRenderer<Type extends LiteralUnion<Node['type'], string>>(
      type: Type,
      namespace: string,
      renderer: MasonryEngineNodeRenderer<RendererNodeForType<Node, Type>>,
    ) {
      if (!nodeRendererMap[namespace]) {
        nodeRendererMap[namespace] = {};
      }
      const namespacedRendererMap = nodeRendererMap[namespace] as Record<
        string,
        unknown
      >;
      namespacedRendererMap[type] = renderer as unknown;
    },
  };
};
